import { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react"
import toastError from "../../errors/toastError"
import { toast } from "react-toastify"

import api from "../../services/api"
import { SocketContext } from "../../context/Socket/SocketContext"
import { useHistory } from "react-router-dom/cjs/react-router-dom.min"
import { whatsAppsReducer } from "./reducer"
import {
  DESCONEXAO_SYNC_TIMEOUT_MS,
  criarSessaoFalhaEhPareamentoEsperado,
  normalizarWhatsAppItem,
} from "./whatsappStatusUtils"

let oPromessaSessaoInicialWhatsapp = null

const buscarSessaoWhatsappInicial = async () => {
  if (!oPromessaSessaoInicialWhatsapp) {
    oPromessaSessaoInicialWhatsapp = api
      .get("/whatsapp/?session=0")
      .finally(() => {
        oPromessaSessaoInicialWhatsapp = null
      })
  }
  return oPromessaSessaoInicialWhatsapp
}

/**
 * Contrato Socket.IO :
 * - `company-{companyId}-whatsappSession`: { action, session } — `session` é o DTO do canal
 *   (canalId, status, socketAlive?, qrcode? só em QRCODE, agendaImportacao*); sem credenciais Baileys.
 * - `company-{companyId}-comando-falhou`: { idComando, tipoComando, conexaoId, categoriaFalha,
 *   mensagemErro, dataHora } — ver talk-api/tasks/prd-sincronizacao-conexoes-whatsapp/contrato-socket.json
 */

const useWhatsApps = () => {
  const token = localStorage.getItem("token")
  const history = useHistory()

  const [whatsApps, dispatch] = useReducer(whatsAppsReducer, [])
  const [loading, setLoading] = useState(true)
  const [comandoFalhouAlert, setComandoFalhouAlert] = useState(null)
  const mTimeoutsDesconexaoRef = useRef({})
  const mDesconexaoPendenteRef = useRef(new Set())

  const socketManager = useContext(SocketContext)

  const limparComandoFalhouAlert = useCallback(() => {
    setComandoFalhouAlert(null)
  }, [])

  const refetchWhatsApps = useCallback(async () => {
    try {
      const { data } = await buscarSessaoWhatsappInicial()
      dispatch({ type: "LOAD_WHATSAPPS", payload: data })
      return data
    } catch (err) {
      toastError(err)
      throw err
    }
  }, [])

  const limparTimeoutDesconexao = useCallback((nCanalId) => {
    const nTimer = mTimeoutsDesconexaoRef.current[nCanalId]
    if (nTimer) {
      clearTimeout(nTimer)
      delete mTimeoutsDesconexaoRef.current[nCanalId]
    }
  }, [])

  const aplicarDesconexaoOtimista = useCallback(
    (nCanalId) => {
      dispatch({ type: "OPTIMISTIC_DISCONNECT", payload: nCanalId })
      mDesconexaoPendenteRef.current.add(nCanalId)
      limparTimeoutDesconexao(nCanalId)

      mTimeoutsDesconexaoRef.current[nCanalId] = setTimeout(async () => {
        delete mTimeoutsDesconexaoRef.current[nCanalId]
        if (!mDesconexaoPendenteRef.current.has(nCanalId)) {
          return
        }
        mDesconexaoPendenteRef.current.delete(nCanalId)
        try {
          const mDados = await refetchWhatsApps()
          const oAtualizado = mDados?.find((w) => w.canalId === nCanalId)
          if (oAtualizado?.status === "DISCONNECTED") {
            dispatch({ type: "CLEAR_DISCONNECT_PENDING", payload: nCanalId })
          } else {
            toast.warn(
              "A desconexão foi solicitada, mas o status ainda não foi confirmado. Atualize a página ou tente novamente.",
            )
            dispatch({ type: "CLEAR_DISCONNECT_PENDING", payload: nCanalId })
          }
        } catch {
          dispatch({ type: "CLEAR_DISCONNECT_PENDING", payload: nCanalId })
        }
      }, DESCONEXAO_SYNC_TIMEOUT_MS)
    },
    [limparTimeoutDesconexao, refetchWhatsApps],
  )

  useEffect(() => {
    return () => {
      Object.values(mTimeoutsDesconexaoRef.current).forEach(clearTimeout)
      mTimeoutsDesconexaoRef.current = {}
    }
  }, [])

  useEffect(() => {
    if (!token) {
      history.push("/login")
      return
    }
    setLoading(true)
    const fetchSession = async () => {
      try {
        const { data } = await buscarSessaoWhatsappInicial()
        dispatch({ type: "LOAD_WHATSAPPS", payload: data })
        setLoading(false)
      } catch (err) {
        setLoading(false)
        toastError(err)
      }
    }
    fetchSession()
  }, [])

  useEffect(() => {
    const companyId = localStorage.getItem("companyId")
    if (!companyId) {
      return undefined
    }

    const socket = socketManager.getSocket(companyId)
    const cCanalWhatsapp = `company-${companyId}-whatsapp`
    const cCanalSessao = `company-${companyId}-whatsappSession`
    const cCanalComandoFalhou = `company-${companyId}-comando-falhou`

    const aoAtualizarWhatsapp = (data) => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp })
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId })
      }
    }

    const aoAtualizarSessao = (data) => {
      if (data.action === "update") {
        const oSessao = normalizarWhatsAppItem(data.session)
        if (oSessao?.canalId) {
          mDesconexaoPendenteRef.current.delete(oSessao.canalId)
          limparTimeoutDesconexao(oSessao.canalId)
          dispatch({
            type: "UPDATE_SESSION",
            payload: { ...oSessao, desconexaoPendente: false },
          })
        }
      }
    }

    const aoComandoFalhou = (data) => {
      if (data?.tipoComando === "CRIAR_SESSAO" && criarSessaoFalhaEhPareamentoEsperado(data?.mensagemErro)) {
        return
      }

      const cMensagem = data?.mensagemErro || "Não foi possível concluir a operação na conexão. Tente novamente."
      setComandoFalhouAlert({
        conexaoId: data?.conexaoId,
        mensagemErro: cMensagem,
        categoriaFalha: data?.categoriaFalha,
        tipoComando: data?.tipoComando,
      })
      toast.error(cMensagem, { autoClose: 8000 })
      dispatch({
        type: "COMANDO_FALHOU",
        payload: {
          conexaoId: data?.conexaoId,
          mensagemErro: cMensagem,
          categoriaFalha: data?.categoriaFalha,
          tipoComando: data?.tipoComando,
        },
      })
    }

    socket.on(cCanalWhatsapp, aoAtualizarWhatsapp)
    socket.on(cCanalSessao, aoAtualizarSessao)
    socket.on(cCanalComandoFalhou, aoComandoFalhou)

    return () => {
      socket.off(cCanalWhatsapp, aoAtualizarWhatsapp)
      socket.off(cCanalSessao, aoAtualizarSessao)
      socket.off(cCanalComandoFalhou, aoComandoFalhou)
    }
  }, [socketManager, limparTimeoutDesconexao])

  return {
    whatsApps,
    loading,
    refetchWhatsApps,
    comandoFalhouAlert,
    limparComandoFalhouAlert,
    aplicarDesconexaoOtimista,
  }
}

export default useWhatsApps
